import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {DataView, DataViewColumn, DataViewRow} from '@models/dataview';
import {SharedDataService} from '@services/shared-data.service';
import {DataViewService} from '@services/data-view.service';
import {DataFrameService} from '@services/dataframes.service';
import {DownloadService} from '@services/download.service';
import {forkJoin, of, Subject, Subscription} from 'rxjs';
import {filter, finalize, map, switchMap} from 'rxjs/operators';
import {AutoUnsubscribe} from '../../../../../../decorators/auto-unsubscribe';
import {Company} from '@models/company';
import {Period} from '@models/period';
import {Select2OptionData} from '@components/shared/select2/select2.interface';
import {DocumentFileService} from '@services/document-file.service';
import {ContextMenuDocument} from './view-templated/table/cell/context-menu';
import {isVendorDataSpread} from '@utils/vendor-data';
import {DataViewConfig} from '@models/dataviewconfig';
import {Footnote} from '@models/footnote';
import {HideRowColStack} from '@models/hide-row-col';
import {COMPANY_ENTITLEMENT_DATA, COMPANY_PAGE_VIEW_MODE, COMPANY_TABS, USER_GUIDES, DEFAULT_CURRENCY} from '@utils/constants';
import {CommonFunctions} from '@utils/common-functions';
import {InsightsService} from '@services/insights.service';
import {PeriodOverPeriodAnalysis} from '@models/period-over-period-analysis'
import {UserGuideService} from '@services/user-guide.service';
import {AlertService} from '@services/alert.service';
import {Logger, LoggingService} from '@services/logging.service';
import {NgxPopupComponent, NgxPopupService} from '@components/shared/ngx-popups/ngx-popups/ngx-popups';
import {ConfirmationPopupComponent} from '@components/shared/popups/confirmation/confirmation-popup.component';
import {UserService} from '@services/user.service';
import {CellInformationService} from '@services/shared/cell-information/cell-information.service';
import {BankSettingsService} from '@services/bank-settings.service';
import {PdfDownloadModalComponent} from "@components/shared/pdf-download-modal/pdf-download-modal.component";
import {StatementBuilderMode} from '@utils/enums';
import {LaunchDarklyService} from '@services/launchdarkly.service';
import {StepLabelService} from '@services/step-label.service';
import {SpreadingTemplateService} from "@services/spreading-template.service";
import {MenuItem} from "@components/shared/popover-menu/popover-menu.component";
import {
  UploadDocumentModalComponent
} from "@components/shared/popups/upload-document-modal/upload-document-modal.component";
import {FilterStatementsComponent} from '@components/shared/filter-statements/filter-statements.component';
import {BorrowerService} from '@services/borrower.service';
import {MultiSelectItem} from "@components/shared/popover-menu-multi-select/popover-menu-multi-select.component";
import { DataFrameValue } from '@models/dataframe';
import {InsightAnalysis} from "@models/insights";


@Component({
    selector: 'app-analysis',
    templateUrl: './analysis.component.html',
    styleUrls: ['./analysis.component.scss'],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class AnalysisComponent implements OnInit, AfterViewInit, OnDestroy {

  logger: Logger;
  subsArr$: Subscription[] = [];
  dataViewId: number = null;
  loadingMessage = '';
  loading = true;
  didErrorOccur = false;
  showTemplateReSpreadErrorState = false;
  activeDataView: DataView | null = null;
  visibleColumnMetadata: any;
  periodOptions: Array<Period>;
  periodEntries: Array<Period> = [];
  periodSelectOptions: Array<Select2OptionData>;
  selectedPeriodOption: Period = new Period();

  // A period can be selected before it's loaded.
  static readonly PERIOD_BATCH_SIZE = 15;
  selectedPeriods: Set<string> | null = null;
  loadedPeriods: Set<string> = new Set<string>();
  private periodsFetchPending = false;
  // Prevents two concurrent batch chains.
  private isFetchingPeriods = false;
  // Set right before loadData() when a Statement Builder create/delete should
  // show the full-page spinner and refresh the rows wholesale, even though
  // activeDataView already exists - matches the reload UX for a first load.
  private forceReloadSpinner = false;
  roundToThousands = false;
  roundToThousandsDisabled = false;
  dollarSign = false;
  dollarSignDisabled = false;
  numbers = true;
  numbersDisabled = false;
  commonSize = false;
  commonSizeDisabled = true;
  trendDisabled = false;
  trend = false;
  sourceItems = false;
  sourceItemsDisabled = true;
  removeHiddenDisabled = true;
  company: Company = null;
  dataViewsList: Array<DataView>;
  showBenchmarking = false;
  isGraphShown = false;
  graphItems: any[] = [];
  graphYAxisLabel = '';
  graphItemLabels = '';
  hiddenRows: Set<number> = new Set();
  hiddenColumns: Set<string> = new Set();
  dataViewConfigId: number;
  showHidden = false;
  showHiddenDisabled = true;
  isFootnotesViewOn = false;
  isStatementBuilderFlyoutOpen = false;
  isFilterStatementsFlyoutOpen = false;
  isCommentViewOpen = false;
  selectedColumnForComments: DataViewColumn = null;
  statementBuilderEditMode: StatementBuilderMode;
  frameToBeEditedId: number;
  footnote: Footnote = null;
  activeFootnotePage = 0;
  docFileId = 0;
  cropper = null;
  gotCompany = false;
  showSpreadingInContextMenu = true;
  showCalculating = false;
  showTemplateReSpreadCalculatingView = false;
  retries = 0;
  finishedCalculating = false;
  showCallToAction = false;
  timeout;
  hideRowColUndoStack: HideRowColStack = new HideRowColStack();
  clearUndoDisabled = true;
  embeddedMode = false;
  projectionScenarioTypeLabelOverride = 'Scenario';
  showDownloadWorkbookButton = false;
  referenceSheetExistsForCompanyDefaultTemplate = false;
  referenceSheetKey = ''
  commonSizeDecimalPrecision = 1;
  spreadingTemplateItems: Array<any> = [];

  // Keeps track of the items that have been selected for graphing (or other purposes)
  selectedRows: Array<number> = [];

  documentMenuItems: Array<ContextMenuDocument> = [];

  // Determines whether the user can see dataviews generated directly from the spread rather than just through a template (to support Dan from Capx for now)
  showNormalizedDataViews = false;

  // insights related properties
  showInsights = false;
  insightsIsLoading = false;
  insights: Array<PeriodOverPeriodAnalysis> = [];
  insightEquationComponents: InsightAnalysis;
  insightsPayload = null;
  currentInsightLabel = '';
  multiply_by_factor: string = 'ACTUALS';
  insightsCurrency: string = DEFAULT_CURRENCY;
  insightHighlightRefs: Set<string> = new Set();
  insightHighlightColumnIndex: number = -1;
  insightSelectedRowRef: string = null;

  // Height (px) of the financial-tabs navbar overlaid on the bottom of the
  // page; reserved at the bottom of the graph so its x-axis stays visible.
  readonly GRAPH_NAVBAR_OFFSET_PX = 41;

  // View mode
  pageViewMode = COMPANY_PAGE_VIEW_MODE.NO_ACCESS;
  readonly COMPANY_PAGE_VIEW_MODE = COMPANY_PAGE_VIEW_MODE;
  routeData: Data;
  hasEditSpreadPermission: boolean;
  canViewFinancials: boolean;

  actionMenuItems: MenuItem[];

  //empty state
  noFinancialsExistIcon = 'fa-magnifying-glass-chart';
  noFinancialsExistHeaderText = 'No Financials Uploaded';
  noFinancialsExistHelpText = "Get started by uploading and spreading a document.";
  noFinancialsCompleteHeaderText = ' No Spreads Complete';
  noFinancialsCompleteHelpText = "View or upload more documents to continue spreading.";
  showEmptyStateUploadButton = false
  shouldShowEmptyState = false
  hasDocuments=false
  noSourceDataExistsIcon = 'fa-magnifying-glass-chart';
  noSourceDataExistsHeaderText = 'No Source Data';
  noSourceDataExistsHelpText = "No source data exists for this statement.";
  reSpreadErrorIcon = 'fa-warning';
  reSpreadErrorHeaderText = 'Template Update Error';
  reSpreadErrorHelpText = 'Please go to the documents tab and re-complete any spread by clicking the link in the actions column then clicking complete. If the issue persists, please contact your administrator.'
  reSpreadUpdateWarningModalText: string;
  reSpreadDocumentErrorWarningModalText = 'Template updates requested by your financial institution are complete. However, during this process, an error occurred with one or more documents uploaded to this borrower.\n\nPlease check the financial summary to ensure all data is there. If any data is missing, navigate to the relevant document and re-complete the spread. If the document cannot be completed, try deleting and re-uploading it.'
  reportingIntervalFilter: MultiSelectItem[] = [];
  yearsFilter: MultiSelectItem[] = [];
  scenarioFilter: MultiSelectItem[] = [];
  monthsCoveredFilter: MultiSelectItem[] = [];
  shouldResetEverything: boolean = false;
  financialSummaryFilter: {[key: string]: MultiSelectItem[]}
  showFilterStatementsButton: boolean = false;
  viewInsightsV2Enabled: boolean = false;
  hasVerticalScrollbar = false;
  insightsLoadingComplete$ = new Subject<void>();
  
  @ViewChild('analysisTableComponent', {static: false}) analysisTableComponent: ElementRef;
  @ViewChild('insightsV2Component', {static: false}) insightsV2Component: any;
  private isScrolling = false;
  private insightsScrollListener: (() => void) | null = null;
  private analysisTableScrollListener: (() => void) | null = null;

  constructor(
    private _route: ActivatedRoute,
    private _popupService: NgxPopupService,
    private _sharedData: SharedDataService,
    private dataViewService: DataViewService,
    private _dataFrameService: DataFrameService,
    private _downloadService: DownloadService,
    private _documentFileService: DocumentFileService,
    private _changeDetector: ChangeDetectorRef,
    public userGuideService: UserGuideService,
    private _insightsService: InsightsService,
    private alertService: AlertService,
    private _loggingService: LoggingService,
    private userService: UserService,
    private router: Router,
    private cellInformationService: CellInformationService,
    private bankSettingsService: BankSettingsService,
    private launchDarklyService: LaunchDarklyService,
    private _stepLabelService: StepLabelService,
    private spreadingTemplateService: SpreadingTemplateService,
    private popupService: NgxPopupService,
    private _borrowerService: BorrowerService,
  ) {
    this.logger = this._loggingService.rootLogger.newLogger('UserService');
    this.routeData = this._route.snapshot.parent.data;
  }

  ngOnInit() {
    this.userGuideService.add(USER_GUIDES.VIEWING_CHANGE_LOG);
    this.getFeatureFlags();
    this.embeddedMode = this._sharedData.embeddedMode$.value;
    this.parseEntitlementData();
    if ([COMPANY_PAGE_VIEW_MODE.FULL, COMPANY_PAGE_VIEW_MODE.VIEW_ONLY].includes(this.pageViewMode)) {
      this._route.paramMap.subscribe(params => {
        this.cellInformationService.hideCellInformation();
        const dvid = params.get('id');
        if (dvid) {
          const newDataViewId = parseInt(dvid, 10);
          if (this.dataViewId !== newDataViewId) {
            // Each tab has its own loaded window and DataView.
            this.resetDataViewState();
          }
          this.dataViewId = newDataViewId;
          this.loadData(false);
        }
      });

      this.subsArr$.push(this._sharedData.company$.pipe(
        filter(newCompany => this._shouldReloadCompanyData(newCompany)))
        .subscribe((company: Company) => {
          this.gotCompany = true;
          this.company = company;
          // Each borrower has its own DataView - clear the previous
          // borrower's, same as a tab switch, so loadData()/mergeDataView()
          // don't merge the new borrower's data onto a stale one.
          this.resetDataViewState();
          const urlDvid = this._route.snapshot.paramMap.get('id');
          this.dataViewId = urlDvid ? parseInt(urlDvid, 10) : null;
          this.shouldShowEmptyState = !!company.defaultSpreadingTemplate
          this.loadData(true);
          this.loadFiles();
        }),
      );
      this.showSpreadingInContextMenu = !this.embeddedMode;
      this.subsArr$.push(
        this.dataViewService.requestForDataViewToBeSaved.subscribe(requested => {
          if (requested) {
            this.dataViewService.save(this.activeDataView).subscribe(_ => {
              this.alertService.success('Success!');
              this.cellInformationService.hideCellInformation();
            });
          }
        })
      );
    } else {
      this.loading = false;
    }
    this.buildActionMenuItems();
    this.shouldResetEverything = false;
  }

  ngAfterViewInit() {
    this.setupScrollSync();
  }

  setupScrollSync() {
    const analysisTableElement = this.analysisTableComponent?.nativeElement;
    if (!analysisTableElement) {
      return;
    }

    if (this.analysisTableScrollListener) {
      analysisTableElement.removeEventListener('scroll', this.analysisTableScrollListener);
    }

    this.analysisTableScrollListener = () => {
      if (this.isScrolling) {
        return;
      }
      this.isScrolling = true;
      this.syncInsightsScroll(analysisTableElement);
      setTimeout(() => {
        this.isScrolling = false;
      }, 10);
    };
    analysisTableElement.addEventListener('scroll', this.analysisTableScrollListener);
    this.setupInsightsScrollListener();
  }

  setupInsightsScrollListener() {
    const insightsScrollContainer = document.querySelector('.insights-table-scroll-container') as HTMLElement;
    if (!insightsScrollContainer) {
      // Retry after a delay if not found yet
      setTimeout(() => this.setupInsightsScrollListener(), 100);
      return;
    }

    if (this.insightsScrollListener) {
      insightsScrollContainer.removeEventListener('scroll', this.insightsScrollListener);
    }

    this.insightsScrollListener = () => {
      if (this.isScrolling) {
        return;
      }
      this.isScrolling = true;
      this.syncAnalysisTableScroll(insightsScrollContainer);
      setTimeout(() => {
        this.isScrolling = false;
      }, 10);
    };
    insightsScrollContainer.addEventListener('scroll', this.insightsScrollListener);
  }

  syncInsightsScroll(sourceElement: HTMLElement) {
    const insightsScrollContainer = document.querySelector('.insights-table-scroll-container') as HTMLElement;
    if (!insightsScrollContainer) {
      return;
    }
    insightsScrollContainer.scrollLeft = sourceElement.scrollLeft;
  }

  syncAnalysisTableScroll(sourceElement: HTMLElement) {
    const analysisTableElement = this.analysisTableComponent?.nativeElement;
    if (!analysisTableElement) {
      return;
    }
    analysisTableElement.scrollLeft = sourceElement.scrollLeft;
  }

  syncScrollPositionOnOpen() {
    // Sync scroll position and re-establish scroll sync after insights are shown
    // This is because user can scroll analysis table before insights are shown
    // This can ensure the insights table scroll is always same as the analysis table
    const analysisTableElement = this.analysisTableComponent?.nativeElement;
    if (!analysisTableElement) {
      return;
    }

    const el = this.analysisTableComponent?.nativeElement;
    this.hasVerticalScrollbar = el ? el.offsetWidth > el.clientWidth : false;

    this.syncInsightsScroll(analysisTableElement);
  }

  ngOnDestroy() {
    this.stopPolling();
    this.userGuideService.remove(USER_GUIDES.VIEWING_DATA);
    this.userGuideService.remove(USER_GUIDES.REMOVING_DATA);
    this.userGuideService.remove(USER_GUIDES.VIEWING_CHANGE_LOG);
    this.userGuideService.remove(USER_GUIDES.BUILD_STATEMENTS_FROM_EXISTING_DATA);
    
    // Clean up scroll event listeners
    if (this.analysisTableScrollListener && this.analysisTableComponent?.nativeElement) {
      this.analysisTableComponent.nativeElement.removeEventListener('scroll', this.analysisTableScrollListener);
    }
    if (this.insightsScrollListener) {
      const insightsScrollContainer = document.querySelector('.insights-table-scroll-container') as HTMLElement;
      if (insightsScrollContainer) {
        insightsScrollContainer.removeEventListener('scroll', this.insightsScrollListener);
      }
    }
  }

  openReSpreadUsedIncompleteStatementsWarningModal(): void {
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: "FINANCIAL SUMMARY UPDATED",
        text: this.reSpreadUpdateWarningModalText,
        cancelButtonText: '',
        headerIcon: 'fa-list-check'
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            this._borrowerService.resolveReSpreadWarningModal(this.company.id).subscribe((_) => {
            })
          }
        }
      }
    });
  }

  openReSpreadDocumentErrorWarningModal(): void {
    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: "CHECK FINANCIAL SUMMARY",
        text: this.reSpreadDocumentErrorWarningModalText,
        cancelButtonText: '',
        headerIcon: 'fa-list-check'
      }
    });
  }

  buildActionMenuItems() {
    this.actionMenuItems = [
      {
        title: 'Download PDF',
        subtitle: 'Download financial summary to pdf',
        faIconClass: 'fa-file-pdf',
        action: this.onClickPdfDownload.bind(this),
        disabled: false,
        visible: true
      },
      {
        title: 'Download Excel',
        subtitle: 'Download financial summary to excel',
        faIconClass: 'fa-file-excel',
        action: this.downloadAsExcel.bind(this),
        disabled: false,
        visible: true
      },
      {
        title: 'Download Workbook Export',
        subtitle: 'Download a workbook file specific to your bank/business',
        faIconClass: 'fa-file-excel',
        action: this.downloadAsExcelWorkbook.bind(this),
        disabled: false,
        visible: (this.showDownloadWorkbookButton && this.activeDataView?.templated && this.referenceSheetExistsForCompanyDefaultTemplate)
      }];

    if (this.hasEditSpreadPermission) {
      this.actionMenuItems.push(
        {
          divider: true
        });
    }

    this.actionMenuItems.push(
      {
        title: 'Build New Statement',
        subtitle: 'Use this tool to consolidate periods or fill in missing periods',
        faIconClass: 'fa-wrench',
        action: this.openStatementBuilderFlyout.bind(this),
        disabled: false,
        visible: this.hasEditSpreadPermission
      })
  }

  getFeatureFlags() {
    const stepName = this._stepLabelService.getStepLabel('SPREAD_FORMAL');
    this.reSpreadUpdateWarningModalText = `A template update or a document restoration has triggered the regeneration of the financial summary for this borrower. Any edits saved in the ${stepName} step have been applied to the financial summary. Please reach out to your administrator if you have any questions.`;
    this.showFilterStatementsButton = this.launchDarklyService.flags['filter-statements-button'];
    this.viewInsightsV2Enabled = this.launchDarklyService.flags['view-insights-v-2-feature-flag'];
    this.subsArr$.push(this.launchDarklyService.flagChange.subscribe((flags) => {
      this.showFilterStatementsButton = flags['filter-statements-button'];
      this.viewInsightsV2Enabled = flags['view-insights-v-2-feature-flag'];
    }));

    this.showDownloadWorkbookButton = this.launchDarklyService.flags['download-excel-workbook'];
    this.subsArr$.push(this.launchDarklyService.flagChange.subscribe((flags) => {
      this.showDownloadWorkbookButton = flags['download-excel-workbook'];
      this.buildActionMenuItems();
    }));
  }

  _shouldReloadCompanyData(newCompany) {
    return newCompany && newCompany?.id && newCompany?.id !== this.company?.id
  }

  closeFootnotes(): void {
    this.isFootnotesViewOn = false;
  }

  openFootnotesOrSourceBox(footnote: Footnote): void {
    if (this.isStatementBuilderFlyoutOpen) {
      this.alertService.warning('Please close statement builder tool before viewing footnotes.');
      return;
    }
    this.closeFootnotes();
    this._changeDetector.detectChanges();
    this.footnote = footnote;
    this.isFootnotesViewOn = true;
    if (this.footnote) {
      this.cropper = {
        'x1': this.footnote.notesX1,
        'y1': this.footnote.notesY1,
        'x2': this.footnote.notesX2,
        'y2': this.footnote.notesY2,
        'width': this.footnote.imageWidth,
        'height': this.footnote.imageHeight
      };
    }
    this.activeFootnotePage = this.footnote.pageId;
    this.docFileId = this.footnote.docFileId;
  }

  openStatementBuilderFlyout(): void {
    if (this.isFootnotesViewOn) {
      this.alertService.warning('Please close footnotes before using statement builder tool.');
      return;
    }
    if (this.isCommentViewOpen) {
      // Close comment view before to open statement builder
      this.closeCommentView();
    }
    if (this.isFilterStatementsFlyoutOpen) {
      // Close filter panel before opening statement builder
      this.closeFilterStatementsFlyout();
    }
    this.isStatementBuilderFlyoutOpen = true;
  }

  closeStatementBuilderFlyout(newStatementCreated = false): void {
    this.isStatementBuilderFlyoutOpen = false;
    this.statementBuilderEditMode = null;
    this.frameToBeEditedId = null;
    if (newStatementCreated) {
      // A new/edited statement can change the underlying data for already
      // loaded periods, so force them to be re-fetch.
      this.loadedPeriods.clear();
      this.forceReloadSpinner = true;
      this.loadData(true);
    }
  }

  openCommentView(column?: DataViewColumn, cellInformation?: DataFrameValue, cellTitle?: string, cellStatementDate?: string): void {
    if (this.isStatementBuilderFlyoutOpen) {
      // Need to remind user to close statement builder tool instead of directly closing it
      // So that they don't accidentally lose any changes they made in statement builder page
      this.alertService.warning('Please close statement builder tool before using comment view.');
      return;
    }
    if (this.isFootnotesViewOn) {
      // Follow the same logic as statement builder tool
      this.alertService.warning('Please close footnotes before using comment view.');
      return;
    }
    // Close and clear any existing comment if there is any opening comments
    this.closeCommentView();
    
    setTimeout(() => {
      if (column) {
        // Handle column comments
        this.selectedColumnForComments = column;
        this.isCommentViewOpen = true;
      } else if (cellInformation) {
        // Handle cell comments
        this.selectedColumnForComments = null;
        this.isCommentViewOpen = true;
        this.cellInformationService.showCellInformation(cellInformation, cellTitle, cellStatementDate);
      }
    }, 300); // delay to ensure the cell information is visible and showing animation
  }

  closeCommentView(): void {
    this.isCommentViewOpen = false;
    this.selectedColumnForComments = null;
    this.cellInformationService.hideCellInformation();
  }

  /**
   * While we figure out how to manage showing raw/normalized/calc, hardcode
   * here that we're not showing normalized unless the flag is on
   */
  shouldShowDataView(dv): boolean {
    if (this.showNormalizedDataViews) {
      return true;
    }

    const hideDataViews = ['Normalized IS', 'Normalized BS', 'Normalized CF', 'Normalized CV', 'Normalized Cash Flow Statement', 'Normalized Balance Sheet', 'Normalized Income Statement', 'Calculations & Ratios', 'Benchmarks'];

    if (hideDataViews.indexOf(dv.title) >= 0) {
      return false;
    }
    return true;
  }

  formatDataViewName(name: string): string {
    const nameTranslate = {
      'Raw IS': 'Source IS',
      'Raw BS': 'Source BS',
      'Raw CF': 'Source CF',
      'Raw Income Statement': 'Source IS',
      'Raw Balance Sheet': 'Source BS',
      'Raw Cash Flow Statement': 'Source CF',
      'Normalized Balance Sheet': 'Norm. BS',
      'Normalized Income Statement': 'Norm. IS',
      'Normalized Cash Flow Statement': 'Norm. CF',
    };

    if (nameTranslate.hasOwnProperty(name)) {
      return nameTranslate[name];
    }
    return name;
  }

  dataViewRouterLink(viewId) {
    return ['/companies', this.company.uuid, 'financials', 'analysis', viewId];
  }

  /**
   * Clears the currently-loaded DataView and its associated period-loading
   * state. Used whenever the view is about to point at a different DataView
   * entirely (tab switch or borrower switch), so loadData()/mergeDataView()
   * don't merge new data onto a stale one.
   */
  private resetDataViewState(): void {
    this.selectedPeriods = null;
    this.loadedPeriods = new Set<string>();
    this.activeDataView = null;
    this.isFetchingPeriods = false;
  }

  /**
   * Load the data we need for this view:
   *
   * 1) The specified dataview-v2. The first load will implicitly
   *    use the most recent reporting period
   * 2) All available reporting periods for this borrower, so we
   *    can change the root period to recalculate the data in the
   *    table.
   */
  loadData(loadPeriods: boolean = true, loadCompanyData: boolean = false): void {
    if (!this.company) {
      return;
    }

    // The full-page spinner shows on the very first load of a DataView, or
    // when forceReloadSpinner asks for the same first-load-style reload (e.g.
    // Statement Builder just created/deleted a period) - fetchPeriodBatch()
    // mirrors this same condition to know when to clear `loading` again.
    this.loading = this.forceReloadSpinner || !this.activeDataView;
    this.didErrorOccur = false;

    // Captured before the getDataView() call below can populate
    // selectedPeriods (from possibly-stale periodOptions) - reconciliation
    // should only run when there was already a real prior selection (i.e.
    // this is a refresh of an already-loaded statement), not a fresh load.
    const hadSelectedPeriods = this.selectedPeriods !== null;

    if (this.dataViewId) {
      this.getDataView();
    }

    if (loadPeriods) {
      const previousPeriodValues = new Set((this.periodOptions || []).map(period => period.value));
      this.subsArr$.push(forkJoin(
        this.dataViewService.listDataViews(this.company.id, this.company.defaultSpreadingTemplate, true),
        this._dataFrameService.getPeriodsWithData(this.company.id)
      ).subscribe(results => {
        this.dataViewsList = results[0].filter(dv => this.shouldShowDataView(dv))
        this.periodEntries = results[1];
        this.periodOptions = this.dedupePeriodOptions(results[1]);
        this.periodSelectOptions = this.getPeriodSelectionOptions();
        if (hadSelectedPeriods) {
          this.reconcileSelectedPeriods(previousPeriodValues);
          this.saveHiddenKeysForUnselectedPeriods();
        }
        // If there's no DVID specified, use the first non-raw one in this list.
        if (!this.dataViewId) {
          const firstDataView = this.dataViewsList.find(dv => !dv.raw);
          if (firstDataView) {
            this.dataViewId = firstDataView.id;
            this.getDataView();
          } else {
            // No data views exist for this borrower (e.g. no documents) - nothing to fetch.
            this.loading = false;
          }
        } else if (this.periodsFetchPending || this.pendingPeriods().length) {
          // Retry the deferred fetch now that periodOptions is ready, or pick
          // up a period reconcileSelectedPeriods() just added to the selection.
          this.getDataView();
        }
      }));
    }

    if (loadCompanyData) {
      this._borrowerService.getCompanyById(this.company.id).subscribe((company) => {
        this.gotCompany = true;
        this.company = company;
        this.shouldShowEmptyState = !!company.defaultSpreadingTemplate;
      })
    }

    if (this.company.defaultSpreadingTemplate) {
      // check to see if the template associated with the borrower has an excel export reference workbook
      this.spreadingTemplateService.getTemplate(this.company.defaultSpreadingTemplate).subscribe(spreadingTemplate => {
        this.spreadingTemplateItems = spreadingTemplate.items;
        this.referenceSheetExistsForCompanyDefaultTemplate = spreadingTemplate.referenceSheetKey
        this.referenceSheetKey = spreadingTemplate.referenceSheetKey
        this.buildActionMenuItems();
      })
    }
  }

  // periodOptions is oldest-first, so the most recent are the last entries.
  private getDefaultSelectedPeriods(): Set<string> {
    const recent = this.periodOptions.slice(-AnalysisComponent.PERIOD_BATCH_SIZE);
    return new Set(recent.map(period => period.value));
  }

  // Reconciles selectedPeriods/loadedPeriods/activeDataView.columns against a
  // freshly-refetched periodOptions list, so a period created or deleted via
  // the Statement Builder (which never touches selectedPeriods itself) is
  // picked up automatically instead of requiring a manual page refresh.
  private reconcileSelectedPeriods(previousPeriodValues: Set<string>): void {
    if (!this.selectedPeriods) {
      return;
    }
    const currentPeriodValues = new Set(this.periodOptions.map(period => period.value));

    // Newly created periods: select them like any other period so they get fetched.
    currentPeriodValues.forEach(value => {
      if (!previousPeriodValues.has(value)) {
        this.selectedPeriods.add(value);
      }
    });

    // Deleted periods: drop them from selection/loaded state, and prune the
    // now-stale column - mergeColumnsByPeriod() only adds/replaces columns
    // present in an incoming batch, it never removes one on its own.
    previousPeriodValues.forEach(value => {
      if (!currentPeriodValues.has(value)) {
        this.selectedPeriods.delete(value);
        this.loadedPeriods.delete(value);
        if (this.activeDataView) {
          this.activeDataView.columns = this.activeDataView.columns.filter(column => column.reportingPeriod !== value);
        }
      }
    });
  }

  // The backend can return more than one Period entry for the same reporting
  // period value (e.g. multiple documents/frames covering the same period) -
  // selectedPeriods/loadedPeriods are Sets keyed by value, so a duplicate
  // here leaks through into duplicate values in the batch request bodies
  // (reporting_period__in) and the period dropdown. Keeps first occurrence,
  // preserving the oldest-first order periodOptions is expected to have.
  private dedupePeriodOptions(periods: Array<Period>): Array<Period> {
    const seen = new Set<string>();
    const deduped = periods.filter(period => {
      if (seen.has(period.value)) {
        return false;
      }
      seen.add(period.value);
      return true;
    });
    if (deduped.length !== periods.length) {
      this.logger.info('getPeriodsWithData returned duplicate reporting period values', {
        companyId: this.company?.id,
        duplicateCount: periods.length - deduped.length,
      });
    }
    return deduped;
  }

  // Selected periods not yet fetched, in periodOptions order.
  private pendingPeriods(): Array<string> {
    const selectedPeriods = this.selectedPeriods;
    if (!selectedPeriods || !this.periodOptions) {
      return [];
    }
    return this.periodOptions
      .map(period => period.value)
      .filter(value => selectedPeriods.has(value) && !this.loadedPeriods.has(value));
  }

  private nextPeriodBatch(): Array<string> {
    // Take from the end so periods closest to what's shown load first.
    return this.pendingPeriods().slice(-AnalysisComponent.PERIOD_BATCH_SIZE);
  }

  // Merges by period instead of replacing activeDataView wholesale.
  // refreshRows: only true on first load / after a respread - an incremental
  // batch's rows are scoped to its own columns and shouldn't overwrite the rest.
  private mergeDataView(dataView: DataView, refreshRows: boolean): void {
    if (!this.activeDataView) {
      this.activeDataView = dataView;
      return;
    }
    this.activeDataView.calculating = dataView.calculating;
    this.activeDataView.templateRespreadInProgress = dataView.templateRespreadInProgress;
    this.activeDataView.companyInReSpreadErrorState = dataView.companyInReSpreadErrorState;
    this.activeDataView.reSpreadErrorInDocumentProcessingStep = dataView.reSpreadErrorInDocumentProcessingStep;
    this.activeDataView.showReSpreadIncludedIncompleteStatementsWarningModal = dataView.showReSpreadIncludedIncompleteStatementsWarningModal;
    if (refreshRows) {
      this.activeDataView.rows = dataView.rows;
    }
    this.activeDataView.columns = this.mergeColumnsByPeriod(this.activeDataView.columns || [], dataView.columns || []);
  }

  // existing is already sorted; only sort the small incoming batch and
  // merge linearly, instead of re-sorting everything each time.
  private mergeColumnsByPeriod(existing: DataViewColumn[], incoming: DataViewColumn[]): DataViewColumn[] {
    if (!incoming.length) {
      return existing;
    }
    const periodOrder = this.periodOptions
      ? new Map(this.periodOptions.map((period, idx) => [period.value, idx]))
      : new Map<string, number>();
    const orderOf = (column: DataViewColumn) => periodOrder.get(column.reportingPeriod) ?? 0;

    const incomingPeriods = new Set(incoming.map(column => column.reportingPeriod));
    const remainingExisting = existing.filter(column => !incomingPeriods.has(column.reportingPeriod));
    const sortedIncoming = [...incoming].sort((a, b) => orderOf(a) - orderOf(b));

    const merged: DataViewColumn[] = [];
    let i = 0, j = 0;
    while (i < remainingExisting.length && j < sortedIncoming.length) {
      if (orderOf(remainingExisting[i]) <= orderOf(sortedIncoming[j])) {
        merged.push(remainingExisting[i++]);
      } else {
        merged.push(sortedIncoming[j++]);
      }
    }
    while (i < remainingExisting.length) { merged.push(remainingExisting[i++]); }
    while (j < sortedIncoming.length) { merged.push(sortedIncoming[j++]); }
    return merged;
  }

  getDataView(): void {
    if (!this.periodOptions) {
      // periodOptions isn't ready - defer rather than fetching unfiltered.
      this.periodsFetchPending = true;
      return;
    }
    this.periodsFetchPending = false;
    if (!this.periodOptions.length) {
      // No periods have data (e.g. the borrower has no documents) - nothing to fetch.
      this.loading = false;
      return;
    }
    if (!this.selectedPeriods) {
      this.selectedPeriods = this.getDefaultSelectedPeriods();
    }
    if (this.isFetchingPeriods) {
      // Already in flight - it'll pick up new selections on its own.
      return;
    }
    const batch = this.nextPeriodBatch();
    if (!batch.length) {
      return;
    }
    this.isFetchingPeriods = true;
    this.fetchPeriodBatch(batch);
  }

  private fetchPeriodBatch(batch: Array<string>): void {
    // Detects a stale response after a tab switch (Angular won't cancel it).
    const requestedForDataViewId = this.dataViewId;
    // Only the first batch shows the full-page loading state - also true when
    // forceReloadSpinner requested that treatment for this reload. Consume the
    // flag now so it doesn't affect any later, unrelated batch/reload.
    const isFirstLoad = !this.activeDataView || this.forceReloadSpinner;
    this.forceReloadSpinner = false;
    // true means a respread was in progress before this response landed.
    const wasCalculating = this.showCalculating;
    const refreshRows = isFirstLoad || wasCalculating;
    const scrollEl = this.analysisTableComponent?.nativeElement;
    const prevScrollWidth = (!isFirstLoad && scrollEl) ? scrollEl.scrollWidth : null;
    this.subsArr$.push(
      this.dataViewService.readDataView(
        this.dataViewId, this.company.id, this.selectedPeriodOption.value, this.sourceItems,
        null, false, { reporting_period__in: batch }
      )
        .pipe(
          // These requests need to be sequential because the dataViewConfig object can be created by readDataView
          switchMap(dataView => {
            if (this.dataViewId !== requestedForDataViewId) {
              // Tab switched before this landed - abandon it.
              return of({ dataView: null, config: [null], stale: true });
            }
            this.mergeDataView(dataView, refreshRows);
            if (prevScrollWidth !== null && scrollEl) {
              // Force the DOM to update before the async config call below.
              this._changeDetector.detectChanges();
              const widthAdded = scrollEl.scrollWidth - prevScrollWidth;
              if (widthAdded > 0) {
                scrollEl.scrollLeft += widthAdded;
              }
            }
            this.buildActionMenuItems();
            return this.dataViewService.readDataViewConfig(this.dataViewId, this.company.id)
              .pipe(
                map(config => ({ dataView, config }))
              );
          })
        )
        .subscribe(result => {
          const { dataView, config, stale } = result as { dataView: DataView, config: any[], stale?: boolean };
          if (stale || this.dataViewId !== requestedForDataViewId) {
            // Abandoned - don't touch state that now belongs to the new tab.
            return;
          }
          // Scroll already compensated at the first merge above.
          this.mergeDataView(dataView, refreshRows);
          let updatedDataViewConfig = config[0];

        this.userGuideService.add(USER_GUIDES.VIEWING_DATA);
        this.userGuideService.add(USER_GUIDES.REMOVING_DATA);
        this.userGuideService.add(USER_GUIDES.BUILD_STATEMENTS_FROM_EXISTING_DATA);
        if (this.sourceItems) {
          const groupedRows = this.divideRowsByHeaders(this.activeDataView.rows);
          this.handleSourceItems(groupedRows, updatedDataViewConfig);
        }
          
        this.bankSettingsService.getAllBankSettings().subscribe(settings => {
          this.projectionScenarioTypeLabelOverride = settings.projectionScenarioTypeLabelOverride || this.projectionScenarioTypeLabelOverride;
          this.commonSizeDecimalPrecision = settings.commonSizeDecimalPrecision;
        });

        if (this.retries > 15) {
          this.isFetchingPeriods = false;
          this.showCallToAction = true;
          this.showCalculating = false;
          this.didErrorOccur = false;
          this.stopPolling();
          const user = this.userService.user;
          this.logger.info(
            'Coffee cup error.',
            {
              'userTenantName': user.tenant_name,
              'userId': user.id,
              'url': this.router.url
            }
          );
        } else {
          if (this.activeDataView.calculating) {
            this.showCalculating = true;
            this.poll();
            this.retries++;
          } else if (this.activeDataView.templateRespreadInProgress){
            this.showTemplateReSpreadCalculatingView = true;
            this.showCalculating = true;
            this.poll();
            this.retries++;
          } else if (this.activeDataView.companyInReSpreadErrorState){
            this.isFetchingPeriods = false;
            this.stopPolling();
            this.loading = false;
            this.didErrorOccur = true;
            this.showTemplateReSpreadErrorState = true
          } else {
            this.isFetchingPeriods = false;
            this.stopPolling();
            this.finishedCalculating = true;
            this.showCalculating = false;
            if (isFirstLoad) {
              this.loading = true;
            }
            this.checkIfReSpreadWarningModalShouldOpen();
            if (wasCalculating) {
              // A respread can change any loaded period, not just this batch's.
              this.loadedPeriods.clear();
            }
            batch.forEach(period => this.loadedPeriods.add(period));
            setTimeout(() => {
              this.handleDataViewConfigResponse(updatedDataViewConfig);
              this.saveHiddenKeysForUnselectedPeriods();
              this.didErrorOccur = false;
              if (isFirstLoad) {
                this.loading = false;
              }
              if (this.pendingPeriods().length) {
                // Fetch the next batch only after this one has landed.
                this.getDataView();
              }
            })
          }
        }

      }, error => {
        if (this.dataViewId !== requestedForDataViewId) {
          return;
        }
        this.logger.error('Error loading financials data view batch: ' + error.message, {
          'errorObject': error,
          'companyId': this.company?.id,
          'dataViewId': this.dataViewId,
          'sourceItems': this.sourceItems
        });
        this.isFetchingPeriods = false;
        this.loading = false;
        this.didErrorOccur = true;
      }
    ));
  }

  private handleSourceItems(groupedRows: Record<string, { row: DataViewRow, index: number }[]>, updatedDataViewConfig: any): void
  {
    this.hiddenRows.clear();
    // Iterate through the grouped rows and check for hidden source items
    Object.keys(groupedRows).forEach(header => {
      const rows = groupedRows[header];               
        rows.forEach(item => {
          if(item.row.isHidden && item.row.isSourceItemRow)
          {
          if (!this.hiddenRows.has(item.index)) {
            this.hiddenRows.add(item.index);
          }
        }
        });
    });

    // updatedDataViewConfig can be undefined here (see fetchPeriodBatch's `config[0]`) -
    // nothing to sync hidden source-item rows into in that case;
    // handleDataViewConfigResponse() treats the same undefined value as a no-op too.
    if (!updatedDataViewConfig?.hidden_rows) {
      return;
    }

    const lineItemIdsSourceItems: Array<number> = this.mapHiddenRowIdsToLineItemIds();
    for (const lineItemId of lineItemIdsSourceItems) {
      if (!updatedDataViewConfig.hidden_rows.includes(lineItemId)) {
        updatedDataViewConfig.hidden_rows.push(lineItemId);
      }
    }
  }
  
  private divideRowsByHeaders(rows: DataViewRow[]): Record<string, { row: DataViewRow, index: number }[]> {
    const result: Record<string, { row: DataViewRow, index: number }[]> = {};
    // Rows before the first lineItem-bearing header (e.g. a Source Items header
    // row, which has no lineItem) still need a bucket - handleSourceItems() only
    // reads row.isHidden/row.isSourceItemRow, not the group key, so grouping them
    // here is safe even though they don't belong to a real template section.
    const UNGROUPED = 'ungrouped';
    let currentHeader: number | string = UNGROUPED;
    result[currentHeader] = [];

    rows.forEach((row, index) => {
      if (row.rowFormat.header && row.lineItem) {
        // If the row is a header, set it as the current header
        currentHeader = row.lineItem.id;
        result[currentHeader] = [];
      } else {
        // Add the row and its index to the current header's group
        result[currentHeader].push({ row, index });
      }
    });

    return result;
  }

  checkIfReSpreadWarningModalShouldOpen() {
    if (this.activeDataView.reSpreadErrorInDocumentProcessingStep){
      this.openReSpreadDocumentErrorWarningModal();
    }
    if (this.activeDataView.showReSpreadIncludedIncompleteStatementsWarningModal) {
      this.openReSpreadUsedIncompleteStatementsWarningModal();
    }
    this.showTemplateReSpreadCalculatingView = false;
  }

  poll(): void {
    this.timeout = setTimeout(() => {
      // Held true until now so a user action can't start a concurrent
      // overlapping fetch while this same batch is still being retried.
      this.isFetchingPeriods = false;
      this.getDataView()
    }, 3000);
  }

  stopPolling() {
    clearTimeout(this.timeout)
  }

  hideCalculatingLoader() {
    this.showCalculating = false;
  }

  handleDataViewConfigResponse(dvc: any): void {
    if (dvc !== undefined) {
      this.multiply_by_factor = dvc.multiply_by_factor || 'ACTUALS';
      this.dollarSign = dvc.dollar_sign;
      this.numbers = dvc.numbers;
      this.commonSize = dvc.common_size;
      this.trend = dvc.trend;
      this.dataViewConfigId = dvc.id;
      this.hiddenColumns = new Set(dvc.hidden_columns);
      this.financialSummaryFilter = dvc.summary_financials_filter;
      if (!this.financialSummaryFilter) {
        this.financialSummaryFilter = {};
      }
      // Skip while the flyout is open - these arrays come from JSON and have no `action`,
      // which would break its live checkboxes.
      if (!this.isFilterStatementsFlyoutOpen) {
        this.reportingIntervalFilter = this.financialSummaryFilter['reporting_interval_filter'] ?? [];
        this.yearsFilter = this.financialSummaryFilter["years_filter"]
        this.scenarioFilter = this.financialSummaryFilter["scenario_filter"] || [];
      }
      // Lazy migration: Check if scenarios are missing before calling setFilters
      const standardScenarios = ["Historical", "Projection", "Proforma"];
      const hasAllScenarios = standardScenarios.every(scenario => 
        this.scenarioFilter.some(opt => opt.title === scenario)
      );
      // Ensure all scenarios are present (setFilters will add any missing ones)
      this.setFilters();
      // If scenarios were missing, save updated filter to database (lazy migration)
      if (!hasAllScenarios && this.dataViewConfigId !== undefined && this.dataViewConfigId !== null) {
        setTimeout(() => this.updateView(), 0);
      }
      this.mapHiddenLineItemIdsToRowIds(new Set(dvc.hidden_rows));
    }
    this.handleNumbersAndPercent();
    this.showHiddenDisplay();
  }

  mapHiddenLineItemIdsToRowIds(lineItemIds: Set<number>): void {
    this.hiddenRows.clear();
    if (lineItemIds) {
      this.activeDataView.rows.filter((r, idx) => {
        if (r.lineItem && lineItemIds.has(r.lineItem.id)) {
          if (!this.hiddenRows.has(idx)) {
            this.hiddenRows.add(idx);
          }
        }
      });
    }
  }

  mapHiddenRowIdsToLineItemIds(): Array<number> {
    const lineItemIds: Array<number> = [];
    this.hiddenRows.forEach((r, idx) => {
      const row = this.activeDataView.rows[idx];
      if (row?.lineItem) {
        lineItemIds.push(row.lineItem.id);
      }
    });

        return lineItemIds;
  }

  /**
   * Get a list of all the document files for the company so we can show them in the table
   */
  loadFiles() {
    this.subsArr$.push(this._documentFileService.listDocumentFiles(this.company.id, null, false, null, true).subscribe(files => {
      this.hasDocuments = files?.length > 0;
      this.documentMenuItems = files.map(file => {
        return new ContextMenuDocument(file.originalDocumentName, file.id, file.associatedStatements, file.uuid, isVendorDataSpread(file));
      });
    }));
  }

  
  

  
  roundingSelectionChanged(e) {
    this.multiply_by_factor = (e.target as HTMLSelectElement).value;
    this.updateView();
  }

  toggleDollarSign(): void {
    this.dollarSign = !this.dollarSign;
    this.updateView();
  }

  toggleNumbers(): void {
    this.numbers = !this.numbers;
    this.handleNumbersAndPercent();
    this.updateView();
  }

  toggleCommonSize(): void {
    if (!this.commonSize && this.trend) {
      this.trend = false;
    }
    this.commonSize = !this.commonSize;
    this.handleNumbersAndPercent();
    this.updateView();
  }

  toggleTrend(): void {
    if (this.commonSize && !this.trend) {
      this.commonSize = false;
    }
    this.trend = !this.trend;
    this.handleNumbersAndPercent();
    this.updateView();
  }

  toggleSourceItems(): void {
    this.sourceItems = !this.sourceItems;
    this.handleNumbersAndPercent();
    // Changes the row *shape* a fetch returns (adds/removes source-item rows),
    // not just column data, so already-loaded periods must re-fetch AND rows
    // must be refreshed like a first load - otherwise mergeDataView() replaces
    // columns (and their per-row cells arrays) while leaving rows stale,
    // desyncing rows/columns[].cells lengths and crashing table rendering.
    this.loadedPeriods.clear();
    this.forceReloadSpinner = true;
    this.loadData(false, false);
  }

  removeShowAll(): void {
    this.shouldResetEverything = true;
    this.hiddenRows.clear();
    this.hiddenColumns.clear();
    // Unselected periods stay off screen, so they must stay hidden for the exports.
    this.unselectedPeriodKeys().forEach(key => this.hiddenColumns.add(key));
    this.showHidden = false;
    this.showHiddenDisplay();
    this.hideRowColUndoStack.clear();
    this.clearAllFilters();
    this.updateView();
  }

  toggleHidden(): void {
    this.showHidden = !this.showHidden;
  }


  handleNumbersAndPercent(): void {
    // 1. Either one of numbers, commonsize or trend must be selected.
    // 2. CommonSize and trend are mutually exclusive
    // either one can be selected or both can be deselected,
    // but both cannot be selected at the same time.
    // This code could be made more company, setting each flag explicity for clarity
    if (this.numbers && (!this.commonSize && !this.trend)) {
      // Only numbers is chosen, and common size and trend are unchecked
      this.numbersDisabled = true;
      this.commonSizeDisabled = false;
      this.trendDisabled = false;
      this.roundToThousandsDisabled = false;
      this.dollarSignDisabled = false;
      this.sourceItemsDisabled = false;
    } else if (this.commonSize && (!this.numbers && !this.trend)) {
      // Only common size is chosen
      this.commonSizeDisabled = true;
      this.numbersDisabled = false;
      this.trendDisabled = false;
      this.multiply_by_factor = "ACTUALS";
      this.roundToThousandsDisabled = true;
      this.dollarSignDisabled = true;
      this.sourceItemsDisabled = true;
    } else if (this.trend && (!this.numbers && !this.commonSize)) {
      // Only trend is chosen
      this.trendDisabled = true;
      this.numbersDisabled = false;
      this.commonSizeDisabled = false;
      this.multiply_by_factor = "ACTUALS";
      this.roundToThousandsDisabled = true;
      this.dollarSignDisabled = true;
      this.sourceItemsDisabled = true;
    } else {
      // numbers + one of trend or column are chosen
      this.numbersDisabled = false;
      this.commonSizeDisabled = false;
      this.trendDisabled = false;
      this.roundToThousandsDisabled = false;
      this.dollarSignDisabled = false;
      this.sourceItemsDisabled = false;
    }
  }


  updateView(onSettled?: () => void): void {
    // For any edge case reason if dataviewconfig id is a non-zero falsey value
    // we return without updating the backend. This should not effect the user,
    // since we have already updated the browser display.
    // When the page is loaded again the settings and page will be in sync.
    if (!this.dataViewId || (!this.dataViewConfigId && this.dataViewConfigId !== 0)) {
      onSettled?.();
      return;
    }

    this.setFilters()

    const dvc: DataViewConfig = new DataViewConfig(this.dataViewConfigId, this.company.id,
      this.dataViewId, this.numbers,
      this.multiply_by_factor,
      !this.removeHiddenDisabled, this.trend,
      this.commonSize, this.mapHiddenRowIdsToLineItemIds(),
      Array.from(this.hiddenColumns), this.dollarSign, this.activeDataView.title, this.financialSummaryFilter)

    this.subsArr$.push(this.dataViewService.updateDataViewConfig(this.dataViewConfigId, dvc).subscribe({
      next: () => onSettled?.(),
      error: (err) => {
        this.logger.error('Failed to save data view config', { errorObject: err });
        onSettled?.();
      }
    }));
  }

  setFilters() {
    if (!this.scenarioFilter) {
      this.scenarioFilter = [];
    }
    const standardScenarios = ["Historical", "Projection", "Proforma"];
    standardScenarios.forEach((scenarioTitle) => {
      if (!this.scenarioFilter.some(opt => opt.title === scenarioTitle)) {
        this.scenarioFilter.push({
          id: String(this.scenarioFilter.length),
          title: scenarioTitle,
          action: () => {},
          checked: false,
        });
      }
    });
    this.financialSummaryFilter = {'reporting_interval_filter': this.reportingIntervalFilter, 'years_filter': this.yearsFilter, 'scenario_filter': this.scenarioFilter}
  }

  getPeriodSelectionOptions(): Array<Select2OptionData> {
    return this.periodOptions.map(po => {
      return {
        'id': po.value,
        'text': po.label,
        'additional': po,
      }
    })
  }

  selectPeriod(evt): void {
    this.selectedPeriodOption = evt.data[0].additional as Period;
    // Changes every column's relative calculation - must re-fetch.
    this.loadedPeriods.clear();
    this.loadData(false);
  }

  downloadWithLoading(downloadingMessage: string, errorMessage: string, func: (string) => void) {
    this.loading = true;
    this.didErrorOccur = false;
    this.loadingMessage = downloadingMessage;
    func(errorMessage);
  }

  downloadAsExcel(): void {
    const docName = this.getDownloadDocName('xlsx');
    this.downloadWithLoading('Generating Excel file...', 'Could not download excel', (errMesg) => {
      this.subsArr$.push(this.dataViewService.readDataViewAsSpreadsheet(this.dataViewId, this.company.id,
        this.selectedPeriodOption.value, docName).subscribe(
        () => {
        },
        () => {
          this.alertService.error('Unable to download excel file, please try again or contact administrator.')
          this.loading = false;
          this.loadingMessage = '';
        },
        () => {
          this.loading = false;
          this.loadingMessage = '';
        }
      ));
    });
  }

  downloadAsExcelWorkbook(): void {
    const key_partition = this.referenceSheetKey.split('.')
    const suffix = key_partition[key_partition.length-1]
    const docName = this.getDownloadDocName(suffix);
    this.downloadWithLoading('Generating Excel file...', 'Could not download excel', (errMesg) => {
      this.subsArr$.push(this.dataViewService.exportExcelReferenceSheet(this.dataViewId, this.company.id, docName).subscribe(
        () => {
        },
        () => {
          this.alertService.error('Unable to download excel file, please try again or contact administrator.')
          this.loading = false;
          this.loadingMessage = '';
        },
        () => {
          this.loading = false;
          this.loadingMessage = '';
        }
      ));
    });
  }

  onClickPdfDownload() {
    if (this.embeddedMode) {
      this.downloadPdf();
    } else {
      this.openPdfDownloadModal();
    }
  }

  downloadPdf(): void {
    const docName = this.getDownloadDocName('pdf');
    this.downloadWithLoading('Generating PDF file...', 'Could not download pdf', (errMesg) => {
      this.subsArr$.push(this.dataViewService.readDataViewAsPDF(this.dataViewId, this.company.id,
        this.selectedPeriodOption.value, docName).subscribe(
        () => {
        },
        () => {
          this.alertService.error('Unable to download PDF file, please try again or contact administrator.')
          this.loading = false;
          this.loadingMessage = '';
        },
        () => {
          this.loading = false;
          this.loadingMessage = '';
        }
      ));
    });
  }

  openPdfDownloadModal() {
    this._popupService.open({
      componentType: PdfDownloadModalComponent,
      cssClass: 'pdf-download-modal-class',
      inputs: {
        company: this.company
      },
    }).then((popup: NgxPopupComponent) => {
      popup.addEventListener('close', (data: CustomEvent) => {
      }, {once: true});
    });
  }

  getDownloadDocName(suffix = 'xlsx'): string {
    let docName = '';
    if (this.activeDataView) {
      if (this.activeDataView.title) {
        docName = this.activeDataView.title;
      }
      if (this.activeDataView.columns.length) {
        if (this.selectedPeriodOption.value) {
          docName += '-' + this.selectedPeriodOption.value.split('.')[0];
        } else {
          docName += '-' + this.activeDataView.columns[this.activeDataView.columns.length - 1].header;
        }
      }
    }
    docName = (docName || 'statement') + '.' + suffix;
    return docName;
  }

  rowToggled(idx: number): void {
    this.activeDataView.rows[idx].selected = !this.activeDataView.rows[idx].selected;

    this.calculateGraphItems();

    // Show the graph if we have at least one item. Hide the graph if we have no items.
    if (this.graphItems.length > 0) {
      this.closeInsights();
      this.isGraphShown = true;
    } else {
      this.isGraphShown = false;
    }
  }

  toggleRowVisibility(idx: number): void {
    const lastHiddenOrShown = new Array<number>();
    let isHide = false;
    if (this.hiddenRows.has(idx)) {
      this.hiddenRows.delete(idx);
      lastHiddenOrShown.push(idx);
      isHide = false;

      let unHideIdx = idx + 1;
      while (this.activeDataView.rows.length > unHideIdx &&
      this.activeDataView.rows[unHideIdx].indentation > this.activeDataView.rows[idx].indentation) {
        if (this.hiddenRows.has(unHideIdx)) {
          this.hiddenRows.delete(unHideIdx);
          lastHiddenOrShown.push(unHideIdx);
        }
        unHideIdx++;
      }
    } else {
      // Remove row from graph if needed
      if (this.activeDataView.rows[idx].selected) {
        this.rowToggled(idx);
      }
      // Hide row and its children
      if (!this.hiddenRows.has(idx)) {
        this.hiddenRows.add(idx);
        lastHiddenOrShown.push(idx);
        isHide = true;
      }
      let hideIdx = idx + 1;
      while (this.activeDataView.rows.length > hideIdx &&
      this.activeDataView.rows[hideIdx].indentation > this.activeDataView.rows[idx].indentation) {
        if (!this.hiddenRows.has(hideIdx)) {
          this.hiddenRows.add(hideIdx);
          lastHiddenOrShown.push(hideIdx);
        }
        hideIdx++;
      }
    }
    if (lastHiddenOrShown.length > 0) {
      this.hideRowColUndoStack.pushRows(isHide, lastHiddenOrShown);
      this.clearUndoDisabled = false;
    }
    this.showHiddenDisplay();
    this.updateView();
  }

  columnHidden(idx: string): void {
    let lastHiddenOrShown = '';
    let isHide = false;
    if (this.hiddenColumns.has(idx)) {
      this.hiddenColumns.delete(idx);
      lastHiddenOrShown = idx;
      isHide = false;
    } else {
      if (!this.hiddenColumns.has(idx)) {
        this.hiddenColumns.add(idx);
        lastHiddenOrShown = idx;
        isHide = true;
      }
    }
    if (lastHiddenOrShown !== '') {
      this.hideRowColUndoStack.pushColumn(isHide, lastHiddenOrShown);
      this.clearUndoDisabled = false;
    }
    this.showHiddenDisplay();
    this.updateView();
  }

  undoLastHidden(): void {
    if (!this.hideRowColUndoStack.isEmpty()) {
      const op = this.hideRowColUndoStack.pop();
      if (op.isRow) {
        const that = this;
        op.rows().forEach(function (value) {
          if (op.isHide) {
            that.hiddenRows.delete(value);
          } else {
            that.hiddenRows.add(value);
          }
        });
      } else {
        if (op.isHide) {
          this.hiddenColumns.delete(op.column());
        } else {
          this.hiddenColumns.add(op.column());
        }
      }

      this.showHiddenDisplay();
      this.clearAllFilters();
      this.updateView();
    }

    this.clearUndoDisabled = this.hideRowColUndoStack.isEmpty();
  }

  clearAllFilters(): void {  
    // Clear all filter arrays
    this.reportingIntervalFilter = [];
    this.yearsFilter = [];
    this.scenarioFilter = [];
    this.monthsCoveredFilter = [];
  }

  // Exports are server-side and honor only the saved hidden columns, so periods that are not
  // selected (never loaded, e.g. older than the default batch) must be saved as hidden even
  // when the user never opens Filter Statements.
  private saveHiddenKeysForUnselectedPeriods(): void {
    // Without a saved config there is nothing to persist to; the next batch retries.
    // While the flyout is open, a save would also persist its unapplied filter edits.
    const hasSavedConfig = this.dataViewConfigId !== null && this.dataViewConfigId !== undefined;
    if (!hasSavedConfig || this.isFilterStatementsFlyoutOpen) {
      return;
    }
    const missingKeys = this.unselectedPeriodKeys().filter(key => !this.hiddenColumns.has(key));
    if (missingKeys.length === 0) {
      return;
    }
    missingKeys.forEach(key => this.hiddenColumns.add(key));
    this.updateView();
  }

  private unselectedPeriodKeys(): string[] {
    const selectedPeriods = this.selectedPeriods;
    if (!selectedPeriods || !this.periodEntries?.length) {
      return [];
    }
    return this.periodEntries
      .filter(period => !selectedPeriods.has(period.value))
      .map(period => period.toColumnKeyString());
  }

  // Hidden keys of periods that were never loaded have no column on screen to show or remove.
  private hasHiddenColumnOnScreen(): boolean {
    const unloadedPeriodKeys = new Set(
      this.periodEntries
        .filter(period => !this.loadedPeriods.has(period.value))
        .map(period => period.toColumnKeyString())
    );
    return Array.from(this.hiddenColumns).some(key => !unloadedPeriodKeys.has(key));
  }

  showHiddenDisplay(): void {
    if (this.hiddenRows.size === 0 && !this.hasHiddenColumnOnScreen()) {
      this.showHidden = false;
      this.showHiddenDisabled = true;
      this.removeHiddenDisabled = true;
    } else {
      this.showHiddenDisabled = false;
      this.removeHiddenDisabled = false;
    }
  }

  closeGraph(): void {
    this.graphItems = [];
    this.activeDataView.rows.forEach(row => {
      row.selected = false;
    });
    this.isGraphShown = false;
  }

  calculateGraphItems(): void {
    // Get only selected rows, and store index so we can use it later to get the correct cell.
    const rowsToShow = this.activeDataView.rows.map((row, idx) => {
      return {
        row: row,
        idx: idx
      };
    }).filter(row => row.row.selected);

    const columnsToShow = this.activeDataView.columns.filter(col => col.showInGraph);
    this.graphItems = rowsToShow.map(row => {
      const seriesValues = columnsToShow.map(col => {
        return {
          name: col.header,
          value: col.cells[row.idx].calculatedValue ? col.cells[row.idx].calculatedValue.value : 0.0,
        }
      });

      return {
        name: row.row.label,
        series: seriesValues,
        idx: row.idx,
        format: row.row.cellFormat.textFormat
      }
    });

    this.generateGraphLabels();
  }

  generateGraphLabels(): void {
    const formats = [];
    if (this.graphItems && this.graphItems.length > 0) {
      this.graphItemLabels = this.graphItems.map(e => e.name).join(', ');

      for (let i = 0; i < this.graphItems.length; i++) {
        let format = this.graphItems[i].format;
        if (!format) {
          format = 'CURRENCY';
        }
        if (!formats.includes(format)) {
          formats.push(format);
        }
      }

      this.graphYAxisLabel = formats.join(', ')
    }
  }

  openInsightsDetail(itemRef: string, insertIdx: number = -1) {
    const updatedPayload = {...this.insightsPayload};
    updatedPayload.lineItemRef = itemRef;
    this.openInsights(updatedPayload, insertIdx);
  }
  
  updateInsightSource(payload) {
    if (!this.showInsights) {
      return
    }
    this.insightsIsLoading = true
    this.openInsights(payload)
  }

  openInsights(payload, insertIdx: number = -1) {
    this.closeGraph();
    this.insightsPayload = payload;
    // @Cursor - Begin
    // Determine the selected column index from the payload
    let selectedColumnIndex = -1;
    if (
      payload &&
      payload.statementDate &&
      this.activeDataView &&
      this.activeDataView.columns
    ) {
      selectedColumnIndex = this.activeDataView.columns.findIndex(
        col =>
          col.statementDate === payload.statementDate &&
          col.scenario === payload.scenario &&
          col.preparationType === payload.preparationType && 
          col.currencyFormat === payload.currencyFormat
          // add more fields if needed
      );

      if (selectedColumnIndex === -1) {
        selectedColumnIndex = this.activeDataView.columns.findIndex(
          col => col.statementDate === payload.statementDate
        );
      }
    }
    if (selectedColumnIndex !== -1 && this.activeDataView.columns[selectedColumnIndex]) {
      this.insightsCurrency = this.activeDataView.columns[selectedColumnIndex].currencyFormat || DEFAULT_CURRENCY;
    } else {
      this.insightsCurrency = DEFAULT_CURRENCY;
    }
    // @Cursor - End
    
    if (this.viewInsightsV2Enabled) {
      this.loadViewInsightsV2(payload);
    } else {
      this.loadLegacyViewInsights(payload, insertIdx)
    }
  }
  
  loadViewInsightsV2(payload) {
    this.visibleColumnMetadata = this.activeDataView.columns
        .map(col => {
      return {
        reportingInterval: col.reportingInterval,
        statementDate: col.statementDate,
        preparationType: col.preparationType,
        scenario: col.scenario,
        projectionName: col.projectionName,
        currencyFormat: col.currencyFormat
      }
    });
    this.insightEquationComponents = null;
    const payloadV2 = {
      lineItemRef: payload.lineItemRef,
      companyId: payload.companyId,
      visibleColumnMetadata: this.visibleColumnMetadata
    };
    
    // Determine the selected column index for highlighting
    this.insightHighlightColumnIndex = this.activeDataView.columns.findIndex(
      col =>
        col.statementDate === payload.statementDate &&
        col.scenario === payload.scenario &&
        col.preparationType === payload.preparationType &&
        col.currencyFormat === payload.currencyFormat &&
        col.projectionName === payload.projectionName
    );
    if (this.insightHighlightColumnIndex === -1) {
      this.insightHighlightColumnIndex = this.activeDataView.columns.findIndex(
        col => col.statementDate === payload.statementDate
      );
    }
    
    // Track the selected row ref (the cell where user clicked to open insights)
    const lineItemRef = payload.lineItemRef;
    this.insightSelectedRowRef = lineItemRef.startsWith('REF_') ? lineItemRef.substring(4) : lineItemRef;
    
    this.subsArr$.push(this._insightsService.getInsightForItem(payloadV2).pipe(
        map(data => data.response.objects[0]),
        finalize(() => {
          this.insightsLoadingComplete$.next();
          this.insightsIsLoading = false;
        })
    ).subscribe(
        response => {
          this.insightEquationComponents = response;
          this._extractInsightHighlightRefs(response);
          this.showInsights = true;
          setTimeout(() => {
            this.syncScrollPositionOnOpen();
            this.setupScrollSync();
          }, 200);
        }, error => {
            this.logger.error('Error in retrieving insights: ' + error.message, {'errorObject': error});
            this.alertService.error('Failed to load insights for item.')
        }
    ))
  }
  
  private _extractInsightHighlightRefs(insight: InsightAnalysis): void {
    // Create a new Set to trigger Angular change detection (OnPush strategy in financials-table)
    const newRefs = new Set<string>();
    
    if (!insight || !insight.components) {
      this.insightHighlightRefs = newRefs;
      return;
    }
    
    // Extract refs from all non-symbol components in the top-level formula
    insight.components.forEach(component => {
      if (!component.isSymbol && component.ref) {
        const baseRef = component.ref.startsWith('REF_') ? component.ref.substring(4) : component.ref;
        newRefs.add(baseRef);
      }
    });
    
    // Assign new Set to trigger change detection
    this.insightHighlightRefs = newRefs;
  }

  loadLegacyViewInsights(payload, insertIdx) {
    this.subsArr$.push(this._insightsService.getPeriodOverPeriodAnalysis(payload).pipe(
        map(data => data.response.objects[0]),
        finalize(() => {
          this.insightsLoadingComplete$.next();
          this.insightsIsLoading = false;
        })
    ).subscribe(
            response => {
              this.insights.length = insertIdx + 1;
              this.insights.push(response);
              this.currentInsightLabel = response.item.label
              this.showInsights = true;
              setTimeout(() => {
                this.syncScrollPositionOnOpen();
                this.setupScrollSync();
              }, 200);
            },
            error => {
              if (error.error_type === 'ObjectDoesNotExist') {
                this.alertService.error('Source statement not found. It may have been deleted.')
              } else {
                this.logger.error('Error in retrieving insights: ' + error.message, {'errorObject': error});
                this.alertService.error('Something went wrong when retrieving detailed data. Our team has been notified and is looking into it')
              }
            }
        ),
    );
  }

  beginDeleteColumn(column: DataViewColumn) {
    if (!column.isDeletable()) {
      this.alertService.error('Column can not be deleted');
      return;
    }

    this._popupService.open({
      componentType: ConfirmationPopupComponent,
      cssClass: 'modal-confirmation',
      inputs: {
        question: 'Are you sure you want to delete this period of data?',
        text: '',
      },
      outputs: {
        callback: (approved: boolean) => {
          if (approved) {
            if (column.preparationType === 'COMBINED') {
              this._dataFrameService.deleteCombinedFrame(column.combinedFrameId).subscribe(() => {
                this.alertService.success('Statement Deleted');
                this.loadedPeriods.clear();
                this.forceReloadSpinner = true;
                this.loadData(true, true)
              }, (err) => {
                if (err?.message) {
                  this.alertService.error(err.message, "Failed to delete period");
                } else {
                  this.alertService.error("Failed to delete period");
                }
                
              })
            } else if (column.preparationType === 'FORECASTED') {
              this._dataFrameService.deleteForecastedFrame(column.forecastedFrameId).subscribe(() => {
                this.alertService.success('Statement Deleted');
                this.loadedPeriods.clear();
                this.forceReloadSpinner = true;
                this.loadData(true, true)
              }, (err) => {
                if (err?.message) {
                  this.alertService.error(err.message, "Failed to delete period");
                } else {
                  this.alertService.error("Failed to delete period");
                }
              })
            } else if (column.preparationType === 'ANNUALIZED') {
              this._dataFrameService.deleteAnnualizedFrame(column.annualizedFrameId).subscribe(() => {
                this.alertService.success('Statement Deleted');
                this.loadedPeriods.clear();
                this.forceReloadSpinner = true;
                this.loadData(true, true)
              }, (err) => {
                this.alertService.error('Failed to delete period');
              })
            } else {
              this._dataFrameService.deleteImportedFrame(column.importedFrameId).subscribe(() => {
                this.alertService.success('Statement Deleted');
                this.loadedPeriods.clear();
                this.forceReloadSpinner = true;
                this.loadData(true, true)
              }, (err) => {
                this.alertService.error('Failed to delete period');
              })
            }
          }
        }
      },
    });
  }

  beginEditColumn(column: DataViewColumn) {
    if (this.isFilterStatementsFlyoutOpen) {
      // Close filter panel before opening statement builder
      this.closeFilterStatementsFlyout();
    }
    if (column.forecastedFrameId) {
      this.statementBuilderEditMode = StatementBuilderMode.GenerateForecast;
      this.frameToBeEditedId = column.forecastedFrameId;
    } else if (column.combinedFrameId) {
      if (column.combinedColumnStatementBuilderOriginType === "CONSOLIDATED") {
        this.statementBuilderEditMode = StatementBuilderMode.Consolidate;
        this.frameToBeEditedId = column.combinedFrameId;
      } else if (column.combinedColumnStatementBuilderOriginType === "CALCULATED") {
        this.statementBuilderEditMode = StatementBuilderMode.CalcMissingPeriod;
        this.frameToBeEditedId = column.combinedFrameId;
      }
    }
    this.isStatementBuilderFlyoutOpen = true;
  }

  beginDuplicateColumn(column: DataViewColumn) {
    if (this.isFilterStatementsFlyoutOpen) {
      // Close filter panel before opening statement builder
      this.closeFilterStatementsFlyout();
    }
    if (column.forecastedFrameId) {
      this.statementBuilderEditMode = StatementBuilderMode.DuplicateForecast;
      this.frameToBeEditedId = column.forecastedFrameId;
      this.isStatementBuilderFlyoutOpen = true;
    }
  }

  closeInsightsDetail(evt, closeIdx) {
    this.insights.length = closeIdx + 1;
  }

  closeInsights() {
    this.showInsights = false;
    this.insights = [];
    this.currentInsightLabel = '';
    // Create new Set to trigger change detection
    this.insightHighlightRefs = new Set<string>();
    this.insightHighlightColumnIndex = -1;
    this.insightSelectedRowRef = null;
  }

  parseEntitlementData() {
    this.hasEditSpreadPermission = this.embeddedMode ? true : this.routeData.companyEntitlement[COMPANY_TABS.BORROWER_NAV][COMPANY_ENTITLEMENT_DATA.SHOW_BUILD_NEW_STATEMENT_BUTTON];
    this.pageViewMode = this.embeddedMode ? COMPANY_PAGE_VIEW_MODE.FULL : this.routeData.companyEntitlement[COMPANY_TABS.FINANCIALS][COMPANY_ENTITLEMENT_DATA.PAGE_VIEW_MODE];
    this.canViewFinancials = [COMPANY_PAGE_VIEW_MODE.VIEW_ONLY, COMPANY_PAGE_VIEW_MODE.FULL].includes(this.pageViewMode);
    this.showEmptyStateUploadButton = this.embeddedMode ? false : this.routeData.companyEntitlement[COMPANY_TABS.BORROWER_NAV][COMPANY_ENTITLEMENT_DATA.SHOW_REQUEST_STATEMENTS_BUTTON]
  }

  get accessErrorText(): string {
    return CommonFunctions.companyPageAccessErrorText(this.pageViewMode);
  }

  get hasAccessError(): boolean {
    return [COMPANY_PAGE_VIEW_MODE.NO_ACCESS, COMPANY_PAGE_VIEW_MODE.ENTITLEMENT_UNAVAILABLE].includes(this.pageViewMode);
  }


    openUploadDocumentModal() {
    this.popupService.open({
      componentType: UploadDocumentModalComponent,
      cssClass: 'doc-upload-modal',
      inputs: {
        company: this.company
      }
    }).then((popup: NgxPopupComponent) => {
      popup.addEventListener('close', (data: CustomEvent) => {
        if (typeof(data.detail) === 'object' && data.detail?.reOpenModal){
          this.openUploadDocumentModal();
        }
      }, {once: true});
    });
  }

  openFilterStatementFlyout() {
    if (this.isStatementBuilderFlyoutOpen) {
      // Close statement builder before opening filter panel
      this.closeStatementBuilderFlyout();
    }
    this.isFilterStatementsFlyoutOpen = true
    this.shouldResetEverything = false
  }

  closeFilterStatementsFlyout(data?: any) {
    if (data) {
      if (data.hiddenColumnsPassedIn) {
        this.hiddenColumns = data.hiddenColumnsPassedIn
      }
      this.yearsFilter = data.yearsFilter
      this.scenarioFilter = data.scenarioFilter
      this.reportingIntervalFilter = data.reportingIntervalFilters
      this.monthsCoveredFilter = data.monthsCoveredFilter || []
      this.clearUndoDisabled = data.clearUndoDisabled
      this.showHiddenDisabled = data.showHiddenDisabled
      this.removeHiddenDisabled = data.removeHiddenDisabled
      this.hideRowColUndoStack = data.hideRowColUndoStack
      if (data.selectedPeriods) {
        // Fetch whatever's newly selected; already-loaded periods show instantly.
        // The fetch waits for the hidden keys to be saved, otherwise a re-selected period
        // could come back still marked hidden by the previously saved config.
        this.selectedPeriods = data.selectedPeriods
        this.updateView(() => this.getDataView())
      } else {
        this.updateView()
      }
    }
    this.isFilterStatementsFlyoutOpen = false
  }

  get loadedPeriodCount(): number {
    return this.loadedPeriods.size;
  }

  get totalStatementsCount(): number {
    return this.periodOptions ? this.periodOptions.length : 0;
  }

  get isPartialStatementSelection(): boolean {
    return this.totalStatementsCount > 0 && this.loadedPeriodCount < this.totalStatementsCount;
  }
}
